import {useEffect, useRef, useState} from 'react';
import SpinnerLoading from '../../../../../../components/SpinnerLoading';
import usePatchStoreAd, {
  IPatchStoreAdPayload,
} from '../../../../../../services/hookService/patchStoreAd/usePatchStoreAd';

interface IAvailableQuantityEditButton {
  availableQuantity: number;
  minSellQuantity: number;
  advertisementGuid: string;
  onQuantityChange?: (newQuantity: number) => void;
  onSuccess?: (newQuantity: number) => void;
  delayMs?: number;
  disabled?: boolean;
}

const AvailableQuantityEditButton = ({
  availableQuantity,
  minSellQuantity,
  advertisementGuid,
  onQuantityChange,
  onSuccess,
  delayMs = 1000,
  disabled = false,
}: IAvailableQuantityEditButton) => {
  const [count, setCount] = useState<number | null>(availableQuantity ?? null);

  const initialQuantityRef = useRef(availableQuantity);
  const pendingQuantityRef = useRef(availableQuantity);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Custom success handler that integrates with your hook's callback
  const handleAfterPatch = (variables: IPatchStoreAdPayload) => {
    const newQuantity = variables.availableQuantity;
    initialQuantityRef.current = newQuantity;
    pendingQuantityRef.current = newQuantity;
    onSuccess?.(newQuantity);
  };

  // Use your actual API hook
  const {mutate: patchStoreAd, isLoading: isPatchLoading} =
    usePatchStoreAd(handleAfterPatch);

  useEffect(() => {
    // Only update if the prop actually changed and we're not loading
    if (availableQuantity !== count && !isPatchLoading) {
      setCount(availableQuantity);
      initialQuantityRef.current = availableQuantity;
      pendingQuantityRef.current = availableQuantity;
    }
  }, [availableQuantity]); // Add this dependency

  const triggerDelayedUpdate = (newQuantity: number) => {
    // Clear existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Set new timer for delayed API call
    timerRef.current = setTimeout(() => {
      patchStoreAd({
        advertisementGuid,
        availableQuantity: newQuantity,
      });
    }, delayMs);
  };

  const handleQuantityChange = (newQuantity: number) => {
    if (disabled || isPatchLoading || count === null) return;

    // Update local state immediately (optimistic update)
    setCount(newQuantity);
    pendingQuantityRef.current = newQuantity;

    // Notify parent of change
    onQuantityChange?.(newQuantity);

    // Trigger delayed API update
    triggerDelayedUpdate(newQuantity);
  };

  const increase = () => {
    if (count === null || disabled || isPatchLoading) return;
    const newQuantity = count + 1;
    handleQuantityChange(newQuantity);
  };

  const decrease = () => {
    if (count === null || count < 1 || disabled || isPatchLoading) return;

    const newQuantity = count - 1;
    handleQuantityChange(newQuantity);
  };

  const canDecrease = count !== null && count !== 0 && count > minSellQuantity;

  // Show loading while initializing
  if (count === null) {
    return (
      <div className="flex items-center justify-center w-24 h-9">
        <SpinnerLoading size={20} />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between">
      <div
        className={`
          flex items-center justify-between gap-2
          border-[1px] px-1 py-2 rounded-lg ${
            disabled ? 'border-gray-300' : 'border-farm-shop-500'
          }


          ${disabled ? 'bg-gray-50' : 'bg-white'}
          ${isPatchLoading ? 'opacity-70' : ''}
          transition-all duration-200
          `}
      >
        {/* Increase Button */}
        <button
          type="button"
          onClick={increase}
          disabled={disabled || isPatchLoading}
          className={`
              flex items-center justify-center
              w-5 h-5 rounded-full
              transition-colors duration-200
              focus:outline-none focus:ring-2 focus:ring-farm-shop-300 focus:ring-offset-1
              ${
                disabled || isPatchLoading
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-farm-shop-500 hover:bg-farm-shop-50 active:bg-farm-shop-100'
              }
            `}
          aria-label="Increase quantity"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
        </button>

        {/* Count Display */}
        <div className="relative flex items-center justify-center ">
          {isPatchLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <SpinnerLoading size={16} />
            </div>
          )}
          <span
            className={`
              text-md font-semibold min-w-[1.5rem] text-center
            ${disabled ? 'text-gray-500' : 'text-farm-shop-500'}
            ${isPatchLoading ? 'opacity-0' : 'opacity-100'}
            transition-opacity
          `}
          >
            {count}
          </span>
        </div>
        {/* Decrease Button */}
        <button
          type="button"
          onClick={decrease}
          disabled={disabled || isPatchLoading || !canDecrease}
          className={`
                flex items-center justify-center
                w-5 h-5 rounded-full
                transition-colors duration-200
                focus:outline-none focus:ring-2 focus:ring-farm-shop-300 focus:ring-offset-1
                ${
                  disabled || isPatchLoading || !canDecrease
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-farm-shop-500 hover:bg-farm-shop-50 active:bg-farm-shop-100'
                }
              `}
          aria-label="Decrease quantity"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M20 12H4"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default AvailableQuantityEditButton;
